import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import appSettings from '../../config/settings';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { pickBy } from '../../util/common';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { parse, stringify } from '../../util/urlHelpers';
import { matchPathname, pathByRouteName } from '../../util/routes';
import { ensureCurrentUser } from '../../util/data';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../util/userHelpers';
import { getSearchPageResourceLocatorStringParams } from '../../containers/SearchPage/SearchPage.shared';
import { isRacingThemeTopbarLinkActive, isProfileMenuPageActive, isSearchPageActive } from '../../util/racingThemeTopbarNav';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import {
  ExternalLink,
  LimitedAccessBanner,
  Modal,
  ModalMissingInformation,
  NamedLink,
  OutsideClickHandler,
} from '../../components';

import TopbarMobileMenu from '../../containers/TopbarContainer/Topbar/TopbarMobileMenu/TopbarMobileMenu';
import RacingThemeLogo from '../RacingThemeLogo/RacingThemeLogo';
import RacingThemeRoot from '../RacingThemeRoot/RacingThemeRoot';
import styles from './RacingThemeTopbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;
const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';
const MOBILE_MENU_BUTTON_ID = 'racingMobileMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'racingMobileSearchButton';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};

const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;

const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const RacingThemeTopbarInner = props => {
  const {
    className,
    rootClassName,
    isAuthenticated = false,
    isLoggedInAs,
    authScopes = [],
    authInProgress = false,
    currentUser,
    currentUserHasListings = false,
    currentUserHasOrders = false,
    currentPage,
    notificationCount = 0,
    intl,
    history = { push: () => {} },
    location = { pathname: '/', search: '', state: null },
    onManageDisableScrolling = () => {},
    onResendVerificationEmail = () => {},
    sendVerificationEmailInProgress = false,
    sendVerificationEmailError,
    showGenericError = false,
    config = {},
    routeConfiguration = [],
    currentSearchParams,
    onLogout = () => Promise.resolve(),
  } = props;

  const [mounted, setMounted] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogoutClick = () => {
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const { mobilemenu } = parse(location.search);

  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const profileDropdownLinkClass = page =>
    classNames(
      styles.dropdownLink,
      isProfileMenuPageActive(page, resolvedCurrentPage) && styles.dropdownLinkActive
    );

  const { routeName: searchPageRouteName, pathParams: searchPagePathParams } =
    getSearchPageResourceLocatorStringParams(routeConfiguration, location);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const searchPageIsActive = isSearchPageActive(resolvedCurrentPage);

  return (
    <div className={classNames(styles.root, rootClassName, className)}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogoutClick}
        currentPage={resolvedCurrentPage}
      />

      <RacingThemeRoot>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <NamedLink
              className={styles.logo}
              name="LandingPage"
              aria-label="Raceteams home"
            >
              <RacingThemeLogo />
            </NamedLink>

            <nav className={styles.navLinks} aria-label="Primary">
              {customLinks.map((link, i) => {
                const isActive = isRacingThemeTopbarLinkActive({
                  linkConfig: link,
                  currentPage: resolvedCurrentPage,
                });
                if (link.type === 'internal' && link.route) {
                  return (
                    <NamedLink
                      key={i}
                      className={classNames(isActive && styles.navLinkActive)}
                      name={link.route.name}
                      params={link.route.params}
                      to={link.route.to}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {link.text}
                    </NamedLink>
                  );
                }
                return (
                  <ExternalLink
                    key={i}
                    className={classNames(isActive && styles.navLinkActive)}
                    href={link.href}
                  >
                    {link.text}
                  </ExternalLink>
                );
              })}
            </nav>

            <div className={styles.navSpacer} />

            <div className={styles.navActions}>
              {showSearchForm && (
                <NamedLink
                  className={classNames(styles.searchBtn, searchPageIsActive && styles.searchBtnActive)}
                  name={searchPageRouteName}
                  params={searchPagePathParams}
                  ariaLabel="Search"
                  aria-current={searchPageIsActive ? 'page' : undefined}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" fill="none" />
                    <path d="m20 20-3.5-3.5" fill="none" />
                  </svg>
                </NamedLink>
              )}

              {authenticatedOnClientSide && (
                <NamedLink
                  className={classNames(
                    styles.favoritesBtn,
                    isProfileMenuPageActive('FavoritesPage', resolvedCurrentPage) &&
                      styles.favoritesBtnActive
                  )}
                  name="FavoritesPage"
                  aria-label="Favorites"
                  aria-current={
                    isProfileMenuPageActive('FavoritesPage', resolvedCurrentPage) ? 'page' : undefined
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </NamedLink>
              )}

              {!isAuthenticatedOrJustHydrated && (
                <NamedLink className={styles.btnLogin} name="LoginPage">
                  Log In
                </NamedLink>
              )}

              {!isAuthenticatedOrJustHydrated && (
                <NamedLink className={styles.btnSignup} name="SignupPage">
                  Sign Up
                </NamedLink>
              )}

              {authenticatedOnClientSide && (
                <OutsideClickHandler onOutsideClick={() => setProfileOpen(false)}>
                  <div className={styles.profileMenu}>
                    <button
                      className={styles.profileBtn}
                      onClick={() => setProfileOpen(o => !o)}
                      aria-expanded={profileOpen}
                      aria-label="Profile menu"
                    >
                      {currentUser?.attributes?.profile?.abbreviatedName || '?'}
                    </button>
                    {profileOpen && (
                      <div className={styles.profileDropdown}>
                        <NamedLink
                          className={profileDropdownLinkClass(`InboxPage:${topbarInboxTab}`)}
                          name="InboxPage"
                          params={{ tab: topbarInboxTab }}
                          aria-current={
                            isProfileMenuPageActive(`InboxPage:${topbarInboxTab}`, resolvedCurrentPage)
                              ? 'page'
                              : undefined
                          }
                        >
                          Inbox
                          {notificationCount > 0 && (
                            <span className={styles.notificationBadge}>{notificationCount}</span>
                          )}
                        </NamedLink>
                        <NamedLink
                          className={profileDropdownLinkClass('FavoritesPage')}
                          name="FavoritesPage"
                          aria-current={
                            isProfileMenuPageActive('FavoritesPage', resolvedCurrentPage)
                              ? 'page'
                              : undefined
                          }
                        >
                          Favorites
                        </NamedLink>
                        {showCreateListingsLink && (
                          <NamedLink
                            className={profileDropdownLinkClass('ManageListingsPage')}
                            name="ManageListingsPage"
                            aria-current={
                              isProfileMenuPageActive('ManageListingsPage', resolvedCurrentPage)
                                ? 'page'
                                : undefined
                            }
                          >
                            Your Listings
                          </NamedLink>
                        )}
                        <NamedLink
                          className={profileDropdownLinkClass('ProfileSettingsPage')}
                          name="ProfileSettingsPage"
                          aria-current={
                            isProfileMenuPageActive('ProfileSettingsPage', resolvedCurrentPage)
                              ? 'page'
                              : undefined
                          }
                        >
                          Profile
                        </NamedLink>
                        <NamedLink
                          className={profileDropdownLinkClass('AccountSettingsPage')}
                          name="AccountSettingsPage"
                          aria-current={
                            isProfileMenuPageActive('AccountSettingsPage', resolvedCurrentPage)
                              ? 'page'
                              : undefined
                          }
                        >
                          Settings
                        </NamedLink>
                        <button
                          className={styles.dropdownLogout}
                          onClick={handleLogoutClick}
                        >
                          Log Out
                        </button>
                      </div>
                    )}
                  </div>
                </OutsideClickHandler>
              )}

              <button
                id={MOBILE_MENU_BUTTON_ID}
                className={styles.navBurger}
                aria-label="Menu"
                onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M3 12h18M3 18h18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile layout: Menu (left) - Logo (center) - Search (right) */}
          <div className={styles.mobileNav}>
            <button
              id={MOBILE_MENU_BUTTON_ID}
              className={styles.mobileMenuBtn}
              aria-label="Menu"
              onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>

            <NamedLink
              className={styles.mobileLogo}
              name="LandingPage"
              aria-label="Raceteams home"
            >
              <RacingThemeLogo />
            </NamedLink>

            {showSearchForm && (
              <NamedLink
                id={MOBILE_SEARCH_BUTTON_ID}
                className={classNames(
                  styles.mobileSearchBtn,
                  searchPageIsActive && styles.searchBtnActive
                )}
                name={searchPageRouteName}
                params={searchPagePathParams}
                ariaLabel="Search"
                aria-current={searchPageIsActive ? 'page' : undefined}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" fill="none" />
                  <path d="m20 20-3.5-3.5" fill="none" />
                </svg>
              </NamedLink>
            )}
          </div>
        </header>
      </RacingThemeRoot>

      <Modal
        id="RacingThemeTopbarMobileMenu"
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_MENU_BUTTON_ID}
      >
        {authInProgress ? null : (
          <TopbarMobileMenu
            isAuthenticated={isAuthenticated}
            currentUser={currentUser}
            onLogout={handleLogoutClick}
            notificationCount={notificationCount}
            currentPage={resolvedCurrentPage}
            customLinks={customLinks}
            showCreateListingsLink={showCreateListingsLink}
            inboxTab={topbarInboxTab}
            config={config}
            currentSearchParams={currentSearchParams}
          />
        )}
      </Modal>

      <ModalMissingInformation
        id="MissingInformationReminder"
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      {showGenericError && (
        <div className={styles.genericError} role="alert">
          <FormattedMessage id="Topbar.genericError" />
        </div>
      )}
    </div>
  );
};

const RacingThemeTopbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <RacingThemeTopbarInner
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default RacingThemeTopbar;
